import { BarChart2, Newspaper, Play, Users, Video } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router';
import * as icons from 'simple-icons';

import { Logo } from '@/components/layouts/logo';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarRail,
  useSidebar,
} from '@/components/ui/sidebar';
import { useDefaultTeam } from '@/features/users/api/getDefaultTeam';
import { useSession } from '@/hooks/useSession';

import { NavMain } from './NavMain';
import { NavSecondary } from './NavSecondary';
import { TeamSwitcher } from './TeamSwitcher';
import { createSimpleIcon } from './utils/createSimpleIcon';

const data = {
  navMain: [
    {
      title: 'Server',
      url: '/server',
      icon: Play,
    },
    {
      title: 'Analytics',
      url: '/analytics',
      icon: BarChart2,
    },
    {
      title: '2D Viewer',
      url: '/demo-viewer',
      icon: Video,
    },
  ],
  navSecondary: [
    {
      title: 'News & Articles',
      url: '/article',
      icon: Newspaper,
    },
    {
      title: 'Team Finder',
      url: '/team-finder',
      icon: Users,
    },
    {
      title: 'Discord',
      url: 'https://discord.gg/kvJt68Wutk',
      icon: createSimpleIcon(icons.siDiscord), // Wrap simple-icon data
    },
  ],
};

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { open } = useSidebar();
  const { isLoggedIn } = useSession();
  const { data: defaultTeam, isSuccess } = useDefaultTeam({
    queryConfig: { enabled: !!isLoggedIn },
  });

  return (
    <Sidebar collapsible="icon" {...props}>
      <SidebarHeader className={`${open ? 'py-6' : ''}`}>
        <Logo className="mx-auto min-w-8 max-w-24 justify-center" />
      </SidebarHeader>
      <SidebarContent>
        <NavMain items={data.navMain} />
        <Separator />
        <NavSecondary items={data.navSecondary} />
      </SidebarContent>
      <SidebarFooter>
        {open && isSuccess && !defaultTeam.isPremium && (
          <Card className="mb-4 overflow-hidden bg-background py-2.5">
            <CardHeader className="px-2.5">
              <CardTitle className="whitespace-nowrap text-sm font-semibold text-foreground tracking-tight">
                Get Your Team Subscription
              </CardTitle>
              <CardDescription className="h-8 min-w-48 text-xs">
                Unlock premium CS2 training tools for your team.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-2.5 px-2.5">
              <Button
                asChild
                className="w-full whitespace-nowrap  bg-linear-to-br from-primary to-[#0A368E] text-sm text-white shadow-md transition-all hover:to-[#041b49]"
                size="sm"
              >
                <Link to={'/onboarding'}>Upgrade Now</Link>
              </Button>
            </CardContent>
          </Card>
        )}
        <TeamSwitcher />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
